import { FileDocument } from '@wonderschool/common-base-types';
import { auth } from '../api/firebase/firebase';
import { logError } from '../rollbar';
import { toEnrollmentDocuments } from './enrollmentsUtils';
import { DocumentType } from './types';

const FILE_SERVICE_HOST = import.meta.env.VITE_FILE_SERVICE_HOST;

// The picker's list endpoint (getFiles) returns raw Firestore docs where
// fileURL is persisted as literal null; only the single-record GET /files/:id
// signs a URL from storageReference (CCMS-8064). So documents picked from the
// list must have their URL resolved one by one before they are written into
// the student's enrollment packet — parents open the packet through that url.
export async function fetchFileDocument(fileId: string): Promise<FileDocument | null> {
  const token = await auth().currentUser?.getIdToken();
  if (!token || !FILE_SERVICE_HOST) return null;

  const response = await fetch(`${FILE_SERVICE_HOST}/files/${encodeURIComponent(fileId)}`, {
    headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
  });
  if (!response.ok) return null;

  return (await response.json()) as FileDocument;
}

// Resolves missing fileURLs at selection time, then maps to the packet shape.
// A document whose URL cannot be resolved is still included — without a url
// key (never url: null) — so a transient file-service error doesn't block the
// provider from assembling the packet; the failure is logged for follow-up.
export async function resolveEnrollmentDocuments(
  selectedDocuments: FileDocument[],
  fetchById: (fileId: string) => Promise<FileDocument | null> = fetchFileDocument
): Promise<DocumentType[]> {
  const resolved = await Promise.all(
    selectedDocuments.map(async (document) => {
      if (document.fileURL) return document;

      try {
        const fetched = await fetchById(document.id);
        return fetched?.fileURL ? { ...document, fileURL: fetched.fileURL } : document;
      } catch (error) {
        logError('Enrollment documents - could not resolve file URL', { fileId: document.id, error });
        return document;
      }
    })
  );

  return toEnrollmentDocuments(resolved);
}
